import React, { useState, useEffect } from "react";
import axios from '@/lib/axios';
import { useNavigate, useLocation } from "react-router-dom";
import type Facility from "../../shared/types/Facility";
import type { Applicant, Consumer, FlatRateAgreement } from "../types";
import type { LostNonWarrantyRequest, LostNonWarrantyRequestParams } from "../types";
import { DateFieldBox } from "@/components/DateField";
import InfoDisplay from "@/components/InfoDisplay";
import SidebarNavigation from "@/components/SidebarNavigation";
import Layout from "../../shared/components/Layout";
import AlertMessages from "@/components/AlertMessages";
import type { Alert } from "@/types";
import clearLocalStorage from "@/utils/clearLocalStorage";
import confirmLostNonWarranty from "../api/confirmLostNonWarranty";
import getNewLostNonWarranty from "../api/getNewLostNonWarranty";
import CustCodeField from "@/components/CustCodeField";
import { getFormattedToday } from "@/utils/format";

const LostNonWarrantyNew: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [alert, setAlert] = useState<Alert>({
    open: false,
    messages: [],
  })

  const [facility, setFacility] = useState<Facility>();
  const [lensPrices, setLensPrices] = useState({
    both: {
      price: "",
    },
    oneSide: {
      price: "",
    }
  });

  const [flatRateAgreement, setFlatRateAgreement] = useState<FlatRateAgreement>();
  const [applicant, setApplicant] = useState<Applicant>();
  const [consumer, setConsumer] = useState<Consumer>();
  const[lostNonWarrantyRequest, setLostNonWarrantyRequest] = useState<LostNonWarrantyRequest>({
    plan: "",
    price: "",
    applicationDate: getFormattedToday(true),
  })
  const [custCodeSuffix, setCustCodeSuffix] = useState<string>("");

  const fetchFlatRateAgreemet = (custCode: {custCode: string}) => {
    return axios.post('/facility/flat_rates/lost_non_warranty/fetch_flat_rate_agreement', custCode);
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getNewLostNonWarranty();

        setFacility(response.data.facility);
        setLensPrices(response.data.lensPrices);

        const fromConfirmPage = location.state?.fromConfirmPage;
        if(fromConfirmPage) {
          const storedApplicant = localStorage.getItem("applicant");
          if(storedApplicant) {
            setApplicant(JSON.parse(storedApplicant));
          }

          const storedConsumer = localStorage.getItem("consumer");
          if(storedConsumer) {
            setConsumer(JSON.parse(storedConsumer));
          }

          const storedLostNonWarrantyRequest = localStorage.getItem("lostNonWarrantyRequest");
          if(storedLostNonWarrantyRequest) {
            setLostNonWarrantyRequest(JSON.parse(storedLostNonWarrantyRequest));
          }

          const storedFlatRateAgreement = localStorage.getItem("flatRateAgreement");
          if(storedFlatRateAgreement) {
            setFlatRateAgreement(JSON.parse(storedFlatRateAgreement));
          }

          const storedCustCodeSuffix = localStorage.getItem("custCodeSuffix");
          if(storedCustCodeSuffix) {
            setCustCodeSuffix(JSON.parse(storedCustCodeSuffix));
          }
        } else {
          clearLocalStorage();
        }
      } catch (error) {
        console.error("Error fetching new lost non warranty data:", error);
      }
    }

    fetchData();
  }, [location.state?.fromConfirmPage])

  useEffect(() => {
    if(custCodeSuffix.length === 4) {
      const fetchData = async () => {
        try {
          const response = await fetchFlatRateAgreemet({custCode: `${facility?.flatRateCode}${custCodeSuffix}`});

          setFlatRateAgreement(response.data.flatRateAgreement);
          setApplicant(response.data.applicant);
          setConsumer(response.data.consumer);
          setLostNonWarrantyRequest({...lostNonWarrantyRequest, flatRateAgreementId: response.data.flatRateAgreement.id })
        } catch(error) {
          setFlatRateAgreement(undefined);
          setApplicant(undefined);
          setConsumer(undefined);
          setLostNonWarrantyRequest({...lostNonWarrantyRequest, flatRateAgreementId: undefined })
        }
      }
      fetchData();

    } else {
      setFlatRateAgreement(undefined);
      setApplicant(undefined);
      setConsumer(undefined);
      setLostNonWarrantyRequest({...lostNonWarrantyRequest, flatRateAgreementId: undefined })
    }
  }, [custCodeSuffix]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const params: LostNonWarrantyRequestParams = {
      lostNonWarrantyRequest: lostNonWarrantyRequest,
    }

    try {
      await confirmLostNonWarranty(params);
      setAlert({open: false, messages: []});
      localStorage.setItem("flatRateAgreement", JSON.stringify(flatRateAgreement));
      localStorage.setItem("applicant", JSON.stringify(applicant));
      localStorage.setItem("consumer", JSON.stringify(consumer));
      localStorage.setItem("lostNonWarrantyRequest", JSON.stringify(lostNonWarrantyRequest));
      localStorage.setItem("custCodeSuffix", JSON.stringify(custCodeSuffix));
      localStorage.setItem("custCode", JSON.stringify(`${facility?.flatRateCode}${custCodeSuffix}`));

      navigate(`/facility/flat_rates/lost_non_warranty/confirm`);
      window.scrollTo(0, 0);
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.errors) {
        setAlert({open: true, messages: err.response.data.errors.fullMessages});
        window.scrollTo(0, 0);
      } else {
        setAlert({open: false, messages: []});
        console.log('An unexpected error occurred:', err);
      }
    }
  }

  const displayPlan = (plan: string, price: string): string => {
    if(plan === 'both') return `両眼プラン(${price}円/月)`
    if(plan === 'right') return `片眼プラン(右眼)(${price}円/月)`
    if(plan === 'left') return `片眼プラン(左眼)(${price}円/月)`
    return '';
  }

  return (
    <Layout>
      <main className="wrapper" id="page-facility_login">
        <section className="sec-contact">
          <div className="inner">
            <h1 className="c-ttl__01">紛失・保証外レンズ申込入力</h1>
            <AlertMessages open={alert.open} messages={alert.messages} />
            <div className="contact-box">
              <div className="contact-box__left">
                <div className="mbpc-98 mbsp-32">
                  <form onSubmit={handleSubmit}>
                    <div className="contact-box__form js-item" id="id-01">
                      <h2 className="c-ttl__02">お客様情報</h2>
                      <InfoDisplay label="契約種別" value={"定額制契約"} />
                      <CustCodeField
                        label="お客様ID"
                        placeholder={"1234"}
                        value={custCodeSuffix}
                        onChange={event => setCustCodeSuffix(event.target.value)}
                        facilityCode={facility?.flatRateCode}
                        required={true}
                        itemStart={true}
                        note={"※4桁の数字でご入力"}
                      />
                      <InfoDisplay label="申込者名(カナ)" value={applicant?.nameKana} />
                      <InfoDisplay label="レンズ使用者名(カナ)" value={consumer?.nameKana} />
                    </div>
                    <div className="contact-box__form mbsp-29 js-item h-adr" id="id-02">
                      <h2 className="c-ttl__02">契約詳細</h2>
                      <InfoDisplay label="ご契約プラン" value={displayPlan(flatRateAgreement?.plan ?? '', flatRateAgreement?.price?.toString() ?? '')} />
                      <DateFieldBox
                        label={"レンズ申込日"}
                        pattern={"d{4}-d{2}-d{2}"}
                        value={lostNonWarrantyRequest.applicationDate}
                        onChange={event => setLostNonWarrantyRequest({...lostNonWarrantyRequest, applicationDate: event.target.value })}
                        required={true}
                      />
                      <div className="contact-box__item contact-box__item--start">
                        <label className="contact-box__ttl">
                          <span>お申込レンズ</span>
                          <small>必須</small>
                        </label>
                        <div className="contact-box__ct">
                          <ul className="list-radio">
                            <li className="list-radio__item">
                              <label className="checkbox-item correct-box__form-check">
                                  <input type="radio" checked={lostNonWarrantyRequest.plan === "both"} onChange={() => {
                                    setLostNonWarrantyRequest({...lostNonWarrantyRequest, plan: "both", price: lensPrices?.both?.price })
                                  }} />
                                  <span className="checkmark"></span>両眼 ({lensPrices?.both?.price}円/月)
                              </label>
                            </li>
                            <li className="list-radio__item">
                              <label className="checkbox-item correct-box__form-check">
                                  <input type="radio" checked={lostNonWarrantyRequest.plan === "right"} onChange={() => {
                                    setLostNonWarrantyRequest({...lostNonWarrantyRequest, plan: "right", price: lensPrices?.oneSide?.price })
                                  }} />
                                <span className="checkmark"></span>片眼(右眼)({lensPrices?.oneSide?.price}円/月)
                              </label>
                            </li>
                            <li className="list-radio__item">
                              <label className="checkbox-item correct-box__form-check">
                                <input type="radio" checked={lostNonWarrantyRequest.plan === "left"} onChange={() => {
                                  setLostNonWarrantyRequest({...lostNonWarrantyRequest, plan: "left", price: lensPrices?.oneSide?.price })
                                }} />
                                <span className="checkmark"></span>片眼(左眼)({lensPrices?.oneSide?.price}円/月)
                              </label>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <button type="submit" className="c-btn__01">入力内容の確認</button>
                  </form>
                </div>
              </div>
              <SidebarNavigation items={[
                { id: "id-01", label: "お客様情報" },
                { id: "id-02", label: "契約詳細" },
                ]} />
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
}

export default LostNonWarrantyNew;